import { isArray, mergeWith } from "lodash";
import * as Papa from "papaparse";

import type { ThunkDispatchCommon } from "single-spa/store/types";

import {
    CONTROL_PARSE_VALUE,
    MAX_ROW_NUMBER,
    INVALID_DOMAIN_REG_EX,
    TRIM_PREFIXES_REG_EX,
    TRIM_QUOTES_REG_EX,
    TRIM_SUB_SITE_REG_EX,
    TRIM_WWW_REG_EX,
    WITH_DOT_AND_NOT_A_NUM_REG_EX,
    COLUMNS_THRESHOLD,
    CONTACTS_CONTROL_PARSE_VALUE,
    MAX_CONTACTS_ROW_NUMBER,
} from "../../components/constants";
import type {
    ParsedContactsResult,
    ParseDomainsResult,
    CompanyParsingErrors } from "../../components/types";
import {
    CreateSuppressionListStep
} from "../../components/types";
import { parserConfig } from "../../configs/parserConfig";
import { SuppressionListActions } from "../../store/action-creators";

const isValidDomain = (value: unknown) => {
    if (typeof value !== "string") {
        return false;
    }

    const trimmedValue = value.trim();

    return !trimmedValue.match(INVALID_DOMAIN_REG_EX) && !!trimmedValue.match(WITH_DOT_AND_NOT_A_NUM_REG_EX);
};
function mergeArray(objValue: Record<string, string[]>, srcValue: Record<string, string[]>) {
    if (isArray(objValue)) {
        return objValue.concat(srcValue);
    }
}

const isLinkedInUrl = (value: string) => value.includes("linkedin.com");
const isFullName = (value: string) => /^\w+( \w+){1,4}$/.test(value.trim());

// This parsing algorithm parse first 20 [CONTROL_PARSE_VALUE]
// rows and find how many of the headers values match domain reg ex
// after this key with the MAX value from [domainsCountMap] sets as a [domainColumn]
// and used to parse without check of value rest of the list

export const parseDomains = (
    listFile: File,
    limit = MAX_ROW_NUMBER,
    ignoreMaxValueCheck = false,
    isSameColCheckIgnored = false,
): Promise<ParseDomainsResult> => {
    return new Promise((resolve, reject) => {
        let rowCount = 0;
        let domainsCountMap: Record<string, number> = {};

        let headerNames: string[] = [];

        let allDomainValues: Record<string, string[]> = {};

        let domainColumn = "";
        let noDomainsError = false;
        let columnsError = false;
        const parsedDomains = new Set<string>();
        const smallFileResult: Record<string, string[]> = {};

        const createConfig = () => {
            const wrappedCompleteParse = () => {
                // This functions executes on every complete with error or not
                // So here is handled errors according to PRD

                if (Object.keys(smallFileResult).length) {
                    smallFileResult[domainColumn].forEach((domain) => {
                        parsedDomains.add(domain);
                    });
                    allDomainValues = mergeWith(smallFileResult, allDomainValues, mergeArray);
                }

                const isOverTheLimit = rowCount > limit;

                if (isOverTheLimit || noDomainsError || columnsError || !parsedDomains.size) {
                    const parsingErrors: Partial<CompanyParsingErrors> = {
                        isOverTheLimit: isOverTheLimit,
                        notADomains: noDomainsError || !parsedDomains.size,
                        columnsNumber: columnsError,
                    };
                    reject(parsingErrors);
                    return;
                }
                resolve({
                    parsedDomains: Array.from(parsedDomains.values()),
                    totalRowCount: rowCount,
                    allDomainValues,
                    headerNames,
                    domainColumn,
                });
            };

            const handleErrorParse = () => reject({ format: true });

            const stepHandler = (results: Papa.ParseStepResult<Record<string, string>>, parser: Papa.Parser) => {
                rowCount++;
                const { data } = results;

                if (headerNames.length < 1) {
                    headerNames = Object.keys(data);
                }

                if (Object.keys(domainsCountMap).length === 0) {
                    domainsCountMap = Object.keys(data).reduce(
                        (acc, val) => {
                            acc[val] = 0;
                            return acc;
                        },
                        {} as Record<string, number>,
                    );
                }

                if (rowCount < CONTROL_PARSE_VALUE) {
                    for (const [key, value] of Object.entries(data)) {
                        if (!isValidDomain(value)) {
                            continue;
                        }

                        domainsCountMap[key] = domainsCountMap[key] + 1;

                        if (smallFileResult[key]) {
                            smallFileResult[key].push(value);
                        } else {
                            smallFileResult[key] = [value];
                        }
                    }

                    const columnNames = Object.keys(domainsCountMap);
                    let maxValue = 0;

                    domainColumn = columnNames.reduce((maxValueColumn, columnName) => {
                        if (domainsCountMap[columnName] > maxValue) {
                            maxValue = domainsCountMap[columnName];
                            return columnName;
                        }

                        return maxValueColumn;
                    }, "");

                    if (maxValue > 0 && !ignoreMaxValueCheck) {
                        // check if more than 1 column have 40% or more domain shaped items
                        columnsError = columnNames.some((columnName) => {
                            if (columnName === domainColumn) {
                                return false;
                            }

                            return domainsCountMap[columnName] >= maxValue * COLUMNS_THRESHOLD;
                        });
                    }
                }

                if (rowCount === CONTROL_PARSE_VALUE) {
                    let maxValue = 0;

                    const allDomainColumns = headerNames.filter((name: string) => {
                        return domainsCountMap[name];
                    });

                    allDomainValues = allDomainColumns.reduce(
                        (acc, domainCol) => {
                            acc[domainCol] = [];
                            return acc;
                        },
                        {} as Record<string, string[]>,
                    );

                    if (headerNames.length >= 1) {
                        domainColumn = headerNames.reduce((acc, colName) => {
                            if (domainsCountMap[colName] > maxValue) {
                                maxValue = domainsCountMap[colName];
                                acc = colName;
                            }

                            return acc;
                        }, "");
                        let sameColCheck = false;

                        if (!isSameColCheckIgnored) {
                            const sameCols = headerNames.filter((colName) => {
                                if (colName !== domainColumn) {
                                    if (domainsCountMap[colName] === maxValue) {
                                        return true;
                                    }
                                    if (domainsCountMap[colName] >= Math.round(maxValue * COLUMNS_THRESHOLD)) {
                                        return true;
                                    }
                                }
                                return false;
                            });
                            sameColCheck = !!sameCols.length;
                        }

                        columnsError = sameColCheck;
                    } else {
                        noDomainsError = true;
                    }

                    if (noDomainsError || columnsError) {
                        parser.abort();
                    }
                }

                if (rowCount >= CONTROL_PARSE_VALUE && isValidDomain(data[domainColumn])) {
                    parsedDomains.add(data[domainColumn]);
                }

                if (rowCount >= CONTROL_PARSE_VALUE) {
                    const fileHeaders = Object.keys(data);

                    fileHeaders.forEach((header) => {
                        if (isValidDomain(data[header])) {
                            allDomainValues[header]?.push(data[header]);
                        }
                    });
                }
            };

            const handleTransform = (value: string) => {
                if (typeof value !== "string") {
                    return value;
                }

                const transformedValue = value
                    .trim()
                    .replace(TRIM_PREFIXES_REG_EX, "")
                    .replace(TRIM_WWW_REG_EX, "")
                    .replace(TRIM_SUB_SITE_REG_EX, "")
                    .replace(TRIM_QUOTES_REG_EX, "")
                    .toLocaleLowerCase();

                return transformedValue;
            };

            return {
                ...parserConfig,
                transform: handleTransform,
                complete: wrappedCompleteParse,
                error: handleErrorParse,
                step: stepHandler,
            };
        };

        Papa.parse(listFile, createConfig());
    });
};

export const parseListThunk =
    (listFile: File) =>
    async (dispatch: ThunkDispatchCommon): Promise<ParseDomainsResult> => {
        try {
            const parsingResult = await parseDomains(listFile);
            dispatch(SuppressionListActions.parseListAsync.success(parsingResult));
            dispatch(SuppressionListActions.setCreateListStep(CreateSuppressionListStep.ASSIGN_NAME));
            return parsingResult;
        } catch (error) {
            dispatch(SuppressionListActions.parseListAsync.failure(error));
            return error;
        }
    };

const sortHeaderNames = (headerNames: Record<string, number>) =>
    Object.keys(headerNames).sort((a, b) => headerNames[b] - headerNames[a]);

const isHeadersNotValid = (headerNames: string[], dataRowLength: number) => {
    const regExp = /^_\d+$/;
    return headerNames.filter((header) => header && !regExp.test(header)).length !== dataRowLength;
};

const hasEmptyColumns = (headerNames: string[], fileParsed: Record<string, string[]>) =>
    headerNames.some((header) => !fileParsed[header]?.some((value) => value.trim()));

export const parseLinkedInAndNames = (listFile: File, quotaRemaining: number): Promise<ParsedContactsResult> => {
    return new Promise((resolve, reject) => {
        let rowCount = 0;

        const linkedinHeaderNames: Record<string, number> = {};
        const fullnameHeaderNames: Record<string, number> = {};
        const companyHeaderNames: Record<string, number> = {};

        const headerNames: string[] = [];
        const fileParsed: Record<string, string[]> = {};

        const createConfig = () => {
            const wrappedCompleteParse = () => {
                Object.entries(fileParsed).forEach(([key, values]) => {
                    const valuesToCheck = values.slice(0, CONTACTS_CONTROL_PARSE_VALUE);

                    const linkedinCount = valuesToCheck.filter(isLinkedInUrl).length;
                    const fullNameCount = valuesToCheck.filter(isFullName).length;
                    const companyDomainsCount = valuesToCheck.filter(isValidDomain).length;

                    if (linkedinCount / valuesToCheck.length >= COLUMNS_THRESHOLD) {
                        linkedinHeaderNames[key] = linkedinCount;
                    } else if (fullNameCount / valuesToCheck.length >= COLUMNS_THRESHOLD) {
                        fullnameHeaderNames[key] = fullNameCount;
                    } else if (companyDomainsCount > 0) {
                        companyHeaderNames[key] = companyDomainsCount;
                    }
                });

                const errors = {
                    isOverTheLimit: rowCount > MAX_CONTACTS_ROW_NUMBER,
                    quotaExceeded: rowCount > quotaRemaining,
                    noRequiredFields:
                        (!Object.keys(linkedinHeaderNames).length &&
                            !Object.keys(fullnameHeaderNames).length &&
                            headerNames.length < 3) ||
                        isHeadersNotValid(headerNames, Object.keys(fileParsed).length),
                    emptyColumns: hasEmptyColumns(headerNames, fileParsed),
                };

                if (Object.values(errors).some(Boolean)) {
                    reject(errors);
                    return;
                }

                resolve({
                    linkedinHeaderNames: sortHeaderNames(linkedinHeaderNames),
                    fullnameHeaderNames: sortHeaderNames(fullnameHeaderNames),
                    companyHeaderNames: sortHeaderNames(companyHeaderNames),
                    headerNames,
                    totalRowCount: rowCount,
                    fileParsed,
                });
            };

            const handleErrorParse = () => reject({ format: true });

            const stepHandler = (results: Papa.ParseStepResult<Record<string, string>>) => {
                rowCount++;
                const { data } = results;

                if (headerNames.length === 0) {
                    headerNames.push(...Object.keys(data));
                }

                for (const [key, value] of Object.entries(data)) {
                    if (typeof value !== "string") {
                        continue;
                    }

                    if (Array.isArray(fileParsed[key])) {
                        fileParsed[key].push(value);
                    } else {
                        fileParsed[key] = [value];
                    }
                }
            };

            return {
                ...parserConfig,
                complete: wrappedCompleteParse,
                error: handleErrorParse,
                step: stepHandler,
            };
        };

        Papa.parse(listFile, createConfig());
    });
};
